import { faBarsFilter } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, Tab } from "@mui/material";
import { CorePermissions } from "app/appPermissions";
import { MprcChip, MprcContainer, MprcIconButtonDropdown, MprcRequestErrorReport } from "components";
import { MprcFilterBox, MprcFilterCountInfo, MprcFilterLine } from "components/filter";
import { IconSort } from "components/icons";
import { MprcChipTabs } from "components/MprcChipTabs";
import { MprcSearchTextField, MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { CoreFilterCommand, FeedDataDto, FeedType, GetFeedMyContributionsPayload, KnowledgeFeedOrder } from "generated/CoreApi_v1";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { TFunction } from "i18next";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { AdvancedFilterDropdown, IAdvFilterMenuItem, IAdvFilterParams, IAdvFilterResult } from "views/general/AdvancedFilter";
import { FeedContainer } from "views/knowledge/feed/components";
import { FeedManager, FeedManagerHandle } from "views/knowledge/feed/components/FeedManager";
import { FeedCustomEventProps, subscribe, unsubscribe } from "views/knowledge/feed/FeedEvents";
import { orderMenuOptions } from "../components/FeedFilter";

////////////////////////////////////////////////////////////////////////////////

type MyContributionsFeedType = FeedType.MyContributions | FeedType.MyAnswers | FeedType.MyDraft;

const filterOptions = (feedType: FeedType, advFilters: IAdvFilterResult[], t: TFunction, institutional: boolean): IAdvFilterMenuItem<CoreFilterCommand>[] => {
    const cmds: IAdvFilterMenuItem<CoreFilterCommand>[] = [];

    if (feedType == FeedType.MyContributions) {
        cmds.push({
            value: CoreFilterCommand.KnowledgeBlocked,
            text: t("Feed.MyContributions.Cmd.IsBlocked"),
            group: 1,
            disabled: advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeBlocked).length > 0
        });

        cmds.push({
            value: CoreFilterCommand.KnowledgeExpired,
            text: t("Feed.MyContributions.Cmd.ExpiredPeriod"),
            group: 1,
            disabled: advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeExpired).length > 0
        });

        cmds.push({
            value: CoreFilterCommand.KnowledgeAvailableInTheFuture,
            text: t("Feed.MyContributions.Cmd.FuturePeriod"),
            group: 1,
            disabled: advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeAvailableInTheFuture).length > 0
        });
    }

    if (feedType == FeedType.MyContributions && institutional) {
        cmds.push({
            value: CoreFilterCommand.KnowledgeInstitutional,
            text: t("Feed.MyContributions.Cmd.IsInstitutional"),
            group: 2,
            disabled: advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeInstitutional).length > 0
        });
        cmds.push({
            value: CoreFilterCommand.KnowledgeNonInstitutional,
            text: t("Feed.MyContributions.Cmd.NonInstitutional"),
            group: 2,
            disabled: advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeNonInstitutional).length > 0,
        });
    }

    cmds.push({
        value: CoreFilterCommand.KnowledgeModerationPending,
        text: t("Feed.MyContributions.Cmd.ModerationPending"),
        group: 3,
        disabled: advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeModerationPending).length > 0
    });

    cmds.push({
        value: CoreFilterCommand.KnowledgeIsRejected,
        text: t("Feed.MyContributions.Cmd.IsRejected"),
        group: 3,
        disabled: advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeIsRejected).length > 0
    });

    return cmds;
};

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    selectedOption: FeedType.MyContributions | FeedType.MyAnswers | FeedType.MyDraft;
}

export const FeedContribution: React.FC<IProps> = (props) => {

    const { t } = useTranslation("knowledge");
    const { permissionExplorer } = useSession();
    const location = useLocation();
    const api = useCoreApi();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [count, setCount] = useState<number | null | undefined>();
    const [loading, setLoading] = useState<boolean>(true);
    const [prevPub, setPrevPub] = useState<string | undefined>(location.state?.publicationId);
    const [viewError, setViewError] = useState<any>();

    const feedType = useMemo(() => location.state?.feedType ?? props.selectedOption, [location.state, props]);

    const feedTypeRef = useRef<MyContributionsFeedType>(feedType);
    const searchRef = useRef<MprcSearchTextFieldHandle | null>(null);
    const feedRef = useRef<FeedManagerHandle>(undefined!);
    const viewFilter = useRef<IAdvFilterParams<KnowledgeFeedOrder>>(
        location.state?.filter
        ?? {
            searchText: "",
            order: KnowledgeFeedOrder.Latest,
            advFilters: [],
        } as IAdvFilterParams<KnowledgeFeedOrder>
    );

    const institutionalPublisherAccess = permissionExplorer.hasOne([CorePermissions.KnowledgeInstitutionalPublisher]);

    ////////////////////////////////////////////////////////////////////////////////

    ///

    const handleNewItem = (e: FeedCustomEventProps) => {
        if (feedTypeRef.current == FeedType.MyContributions) feedRef.current.reset();
    };

    ///
    const handleUpdateItem = (e: FeedCustomEventProps) => {
        if (feedTypeRef.current == FeedType.MyDraft) feedRef.current.reset();
    };

    ///
    const handleNewDraftItem = (e: FeedCustomEventProps) => {
        if (feedTypeRef.current == FeedType.MyDraft) feedRef.current.reset();
    };

    ///
    const handleAdvFilterChange = (filter: IAdvFilterParams<KnowledgeFeedOrder>) => {
        viewFilter.current = filter;
        navigate(document.location.search, { replace: true, state: { filter: viewFilter.current, feedType } });
    };

    ///
    const buildFilter = (startIndex: number, endIndex: number): GetFeedMyContributionsPayload => {
        return {
            searchText: viewFilter.current.searchText ?? null,
            isBlocked: viewFilter.current.advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeBlocked).length > 0,
            expiredPeriod: viewFilter.current.advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeExpired).length > 0,
            futurePeriod: viewFilter.current.advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeAvailableInTheFuture).length > 0,
            moderationPending: viewFilter.current.advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeModerationPending).length > 0,
            isRejected: viewFilter.current.advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeIsRejected).length > 0,
            isInstitutional: viewFilter.current.advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeInstitutional).length > 0,
            nonInstitutional: viewFilter.current.advFilters.filter(p => p.command == CoreFilterCommand.KnowledgeNonInstitutional).length > 0,
            orderBy: viewFilter.current.order,
            startPublicationId: location.state?.publicationId,
            startIndex,
            endIndex
        };
    };

    ///
    const onGetData = async (startIndex: number, endIndex: number) => {
        setLoading(true);

        let feedData: FeedDataDto = { items: [], totalRows: 0 };

        if (feedType == FeedType.MyContributions) {
            let result = await api.v1.knowledgeFeed.getFeedMyContributions(buildFilter(startIndex, endIndex));
            feedData = result.data;
        }
        else if (feedType == FeedType.MyAnswers) {
            let result = await api.v1.knowledgeFeed.getFeedMyAnswers(buildFilter(startIndex, endIndex));
            feedData = result.data;
        } else if (feedType == FeedType.MyDraft) {
            let result = await api.v1.knowledgeFeed.getFeedMyDrafts(buildFilter(startIndex, endIndex));
            feedData = result.data;
        }

        setCount(feedData.totalRows);
        setLoading(false);
        return feedData.items;
    };

    ///
    const resetFilters = () => {
        searchRef.current?.reset();
        viewFilter.current.advFilters = [];
    };

    ///
    const resetSort = () => {
        viewFilter.current = { ...viewFilter.current, order: KnowledgeFeedOrder.Latest };
    };

    ///
    const goBackStart = () => {
        navigate(document.location.search, { replace: true, state: { filter: viewFilter.current, feedType } });
    };

    ///
    // useEffect(() => {
    //     if (count === null) {
    //         navigate(document.location.search, { replace: true, state: { filter: viewFilter.current, feedType } });
    //     }
    // }, [count]);

    ///
    useDidUpdateEffect(() => {
        ///
        if (prevPub != location.state?.publicationId) {
            setPrevPub(undefined);
        }

        if (location.state != null) feedRef.current?.reset();

        ///
    }, [location.state]);

    ///
    useDidUpdateEffect(() => {
        if (!prevPub) {
            resetSort();
            resetFilters();
        }
    }, [feedType]);

    /// Retry
    useDidUpdateEffect(() => {
        if (viewError == null) feedRef.current.reset();
    }, [viewError]);

    ///
    useEffect(() => {
        subscribe("PublicationCreated", handleNewItem);
        subscribe("PublicationDraftCreated", handleNewDraftItem);
        subscribe("PublicationUpdate", handleUpdateItem);

        ///
        const filter = searchParams.get("f");
        if (filter != null) {
            if (filter == "rejected") {
                const af = viewFilter.current.advFilters.find(p => p.value == CoreFilterCommand.KnowledgeIsRejected);
                if (af == null)
                    viewFilter.current.advFilters.push({
                        command: CoreFilterCommand.KnowledgeIsRejected,
                        value: true,
                        text: t("Feed.MyContributions.Cmd.IsRejected"), quickFilter: null
                    });
            }
            searchParams.delete("f");
            setSearchParams(searchParams);
        }

        feedRef.current?.reset();

        /// Cleanup
        return () => {
            unsubscribe("PublicationCreated", handleNewItem);
            unsubscribe("PublicationDraftCreated", handleNewDraftItem);
            unsubscribe("PublicationUpdate", handleUpdateItem);
        };
    }, []);

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    const orderOptions = feedType == FeedType.MyDraft
        ? orderMenuOptions(t).filter(o => o.value == KnowledgeFeedOrder.Latest || o.value == KnowledgeFeedOrder.Title)
        : orderMenuOptions(t).filter(o => o.value != KnowledgeFeedOrder.MostRelevant);

    const filtered = viewFilter.current.searchText.length > 0 || viewFilter.current.advFilters.length > 0;

    const Header = (
        <Box>
            <MprcContainer>
                <MprcFilterBox>
                    <MprcFilterLine>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} width="100%">
                            <MprcChipTabs
                                value={feedType}
                            >
                                <Tab
                                    label={t("Feed.MyContributions.PublicationsChoiceText")}
                                    value={FeedType.MyContributions}
                                    onClick={() => navigate(KnowledgePaths.FeedMyContributions)}
                                />
                                <Tab
                                    label={t("Feed.MyContributions.AnswersChoiceText")}
                                    value={FeedType.MyAnswers}
                                    onClick={() => navigate(KnowledgePaths.FeedMyAnswers)}
                                />
                                <Tab
                                    label={t("Feed.MyContributions.DraftsChoiceText")}
                                    value={FeedType.MyDraft}
                                    onClick={() => navigate(KnowledgePaths.FeedMyDrafts)}
                                />
                            </MprcChipTabs>

                            <div>
                                {feedType != FeedType.MyDraft &&
                                    <AdvancedFilterDropdown
                                        textOrIcon={<FontAwesomeIcon icon={faBarsFilter} />}
                                        color={viewFilter.current.advFilters.length == 0 ? "inherit" : "warning"}
                                        size="medium"
                                        items={filterOptions(feedType, viewFilter.current.advFilters, t, institutionalPublisherAccess)}
                                        onChange={option => handleAdvFilterChange(
                                            {
                                                ...viewFilter.current,
                                                advFilters: [
                                                    ...viewFilter.current.advFilters,
                                                    { command: option.value, value: true, text: option.text, quickFilter: null }
                                                ]
                                            }
                                        )}
                                    />
                                }

                                <MprcIconButtonDropdown
                                    menuItemCheck
                                    icon={<IconSort />}
                                    title={t("shared:Filter.OrderBy")}
                                    items={orderOptions}
                                    selected={viewFilter.current.order}
                                    onChange={order => handleAdvFilterChange({ ...viewFilter.current, order })} />
                            </div>
                        </Stack>
                    </MprcFilterLine>
                    <MprcFilterLine>
                        <MprcSearchTextField
                            ref={searchRef}
                            confirmMode="onEnter"
                            onConfirm={(value) => {
                                handleAdvFilterChange({ ...viewFilter.current, searchText: value });
                            }}
                            placeholder={t("Feed.MyContributions.SearchTextPlaceholder")}
                        />
                    </MprcFilterLine>

                    {viewFilter.current.advFilters.length > 0 &&
                        <MprcFilterLine>
                            <Stack
                                direction="row"
                                justifyContent="flex-start"
                                alignContent="flex-start"
                                flexWrap="wrap"
                                mt={2}
                            >
                                {
                                    viewFilter.current.advFilters.map((item, index) => (
                                        <MprcChip
                                            key={`${item.command}${index}`}
                                            sx={{ margin: "4px" }}
                                            tabIndex={-1}
                                            label={item.text}
                                            size="small"
                                            onDelete={() => handleAdvFilterChange(
                                                {
                                                    ...viewFilter.current,
                                                    advFilters: viewFilter.current.advFilters.filter(p => p.command != item.command)
                                                }
                                            )}
                                        />
                                    ))
                                }
                            </Stack>
                        </MprcFilterLine>
                    }
                </MprcFilterBox>
            </MprcContainer>
            {
                loading
                    ? null
                    : <MprcFilterCountInfo
                        text={t(filtered ? "Feed.MyContributions.RecordsFoundText" : "Feed.MyContributions.RecordsText", { count: count ?? 0 })}
                        variant={filtered ? "bodyNotice" : count == 0 ? "captionHeader" : "caption"}
                        onResetFilter={filtered ? resetFilters : undefined}
                    />
            }
        </Box >
    );

    ///
    return viewError
        ? <MprcRequestErrorReport
            error={viewError}
            onRetry={() => setViewError(null)}
        />
        : feedType == FeedType.MyContributions
            ? <FeedContainer
                header={Header}
                feed={
                    <FeedManager
                        onGobackTop={goBackStart}
                        previousPublicationId={prevPub}
                        feedType={FeedType.MyContributions}
                        onGetData={onGetData}
                        onError={(error) => setViewError(error)}
                        ref={feedRef}
                        useInfiniteLoading
                    />
                }
            />
            : feedType == FeedType.MyAnswers
                ? <FeedContainer
                    header={Header}
                    feed={
                        <FeedManager
                            onGobackTop={goBackStart}
                            previousPublicationId={prevPub}
                            feedType={FeedType.MyAnswers}
                            onGetData={onGetData}
                            onError={(error) => setViewError(error)}
                            ref={feedRef}
                            useInfiniteLoading
                        />
                    }
                />
                : feedType == FeedType.MyDraft
                    ? <FeedContainer
                        header={Header}
                        feed={
                            <FeedManager
                                onGobackTop={goBackStart}
                                previousPublicationId={prevPub}
                                feedType={FeedType.MyDraft}
                                onGetData={onGetData}
                                onError={(error) => setViewError(error)}
                                ref={feedRef}
                                onReload={() => feedRef.current?.reset()}
                                useInfiniteLoading
                            />
                        }
                    />
                    : null;
};
